/**
 * UI Action Handler
 * Executes UI actions returned by the chat API
 */
class UIActionHandler {
  constructor() {
    this.handlers = new Map();
    this.navigationCallback = null;
    this.sceneCallback = null;
    this.modalCallback = null;
    this.toastCallback = null;
  }

  /**
   * Set navigation callback
   * @param {Function} callback - Navigation function
   */
  setNavigationCallback(callback) {
    this.navigationCallback = callback;
  }

  /**
   * Set scene update callback
   * @param {Function} callback - Scene update function
   */
  setSceneCallback(callback) {
    this.sceneCallback = callback;
  }

  /**
   * Set modal callback
   * @param {Function} callback - Modal display function
   */
  setModalCallback(callback) {
    this.modalCallback = callback;
  }

  /**
   * Set toast notification callback
   * @param {Function} callback - Toast display function
   */
  setToastCallback(callback) {
    this.toastCallback = callback;
  }

  /**
   * Execute a UI action
   * @param {Object} action - UI action to execute
   * @returns {Promise<void>}
   */
  async executeAction(action) {
    if (!action || !action.type) {
      console.warn('Invalid UI action:', action);
      return;
    }

    try {
      switch (action.type) {
        case 'navigate':
          await this.handleNavigate(action);
          break;

        case 'navigateBack':
          await this.handleNavigateBack();
          break;

        case 'updateScene':
          await this.handleSceneUpdate(action);
          break;

        case 'addObject':
          await this.handleAddObject(action);
          break;

        case 'removeObject':
          await this.handleRemoveObject(action);
          break;

        case 'updateTexture':
          await this.handleUpdateTexture(action);
          break;

        case 'focusCamera':
          await this.handleFocusCamera(action);
          break;

        case 'setCameraView':
          await this.handleSetCameraView(action);
          break;

        case 'showModal':
          await this.handleShowModal(action);
          break;

        case 'hideModal':
          await this.handleHideModal();
          break;

        case 'showToast':
          await this.handleShowToast(action);
          break;

        case 'showSidebar':
          await this.handleShowSidebar(action);
          break;

        case 'hideSidebar':
          await this.handleHideSidebar();
          break;

        case 'refreshData':
          await this.handleRefreshData(action);
          break;

        case 'updateLocalState':
          await this.handleUpdateLocalState(action);
          break;

        case 'chain':
          await this.handleChainedActions(action);
          break;

        case 'triggerExport':
          await this.handleTriggerExport(action);
          break;

        case 'toggleFullscreen':
          await this.handleToggleFullscreen(action);
          break;

        case 'clearChat':
          await this.handleClearChat(action);
          break;

        case 'logout':
          await this.handleLogout(action);
          break;

        case 'triggerCustomAction':
          await this.handleTriggerCustomAction(action);
          break;

        case 'openRenderingModal':
          await this.handleOpenRenderingModal(action);
          break;

        // `switch_view_mode` and `open_checkpoints_modal` answer with a
        // window event the viewers already listen for.
        case 'dispatchEvent':
          window.dispatchEvent(
            new CustomEvent(action.eventName, { detail: action.detail })
          );
          break;

        default:
          console.warn('Unknown UI action type:', action.type);
      }
    } catch (error) {
      console.error('Error executing UI action:', error);
      throw error;
    }
  }

  /**
   * Execute multiple UI actions
   * @param {Array} actions - Array of UI actions
   * @returns {Promise<void>}
   */
  async executeActions(actions) {
    if (!Array.isArray(actions)) {
      return;
    }

    for (const action of actions) {
      await this.executeAction(action);
    }
  }

  // Navigation handlers
  async handleNavigate(action) {
    if (this.navigationCallback) {
      await this.navigationCallback(action.path, action);
    } else {
      // Fallback to window navigation
      window.location.href = action.path;
    }
  }

  async handleNavigateBack() {
    if (this.navigationCallback) {
      await this.navigationCallback(-1);
    } else {
      window.history.back();
    }
  }

  // Scene handlers
  async handleSceneUpdate(action) {
    if (this.sceneCallback) {
      await this.sceneCallback('update', action.action, action.data);
    } else {
      console.warn('No scene callback registered');
    }
  }

  async handleAddObject(action) {
    if (this.sceneCallback) {
      await this.sceneCallback('addObject', null, action);
    }
  }

  async handleRemoveObject(action) {
    if (this.sceneCallback) {
      await this.sceneCallback('removeObject', null, action);
    }
  }

  async handleUpdateTexture(action) {
    if (this.sceneCallback) {
      await this.sceneCallback('updateTexture', null, action);
    }
  }

  async handleFocusCamera(action) {
    if (this.sceneCallback) {
      await this.sceneCallback('focusCamera', null, {
        targetId: action.targetId,
        distance: action.distance,
        duration: action.duration,
      });
    }
  }

  async handleSetCameraView(action) {
    if (this.sceneCallback) {
      await this.sceneCallback('setCameraView', null, action);
    }
  }

  // UI component handlers
  async handleShowModal(action) {
    if (this.modalCallback) {
      await this.modalCallback('show', action.modalType, action.data);
    }
  }

  async handleHideModal() {
    if (this.modalCallback) {
      await this.modalCallback('hide');
    }
  }

  async handleShowToast(action) {
    if (this.toastCallback) {
      await this.toastCallback({
        message: action.message,
        severity: action.severity || 'info',
        duration: action.duration || 4000,
      });
    }
  }

  async handleShowSidebar(action) {
    if (this.modalCallback) {
      await this.modalCallback('showSidebar', action.sidebarType, action.data);
    }
  }

  async handleHideSidebar() {
    if (this.modalCallback) {
      await this.modalCallback('hideSidebar');
    }
  }

  // Data handlers
  async handleRefreshData(action) {
    // Trigger data refresh - implementation depends on your data layer
    window.dispatchEvent(
      new CustomEvent('refreshData', { detail: action.data })
    );
  }

  async handleUpdateLocalState(action) {
    // Update local state - implementation depends on your state management
    window.dispatchEvent(
      new CustomEvent('updateLocalState', { detail: action.data })
    );
  }

  // Chain handler
  async handleChainedActions(action) {
    if (Array.isArray(action.actions)) {
      for (const subAction of action.actions) {
        await this.executeAction(subAction);
        // Small delay between chained actions for visual feedback
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    }
  }

  // Action handlers
  async handleTriggerExport(action) {
    // Dispatch custom event for export
    window.dispatchEvent(
      new CustomEvent('triggerExport', {
        detail: { format: action.format },
      })
    );
  }

  async handleToggleFullscreen(action) {
    // Dispatch custom event for fullscreen toggle
    window.dispatchEvent(new CustomEvent('toggleFullscreen'));
  }

  async handleClearChat(action) {
    // Dispatch custom event to clear chat
    window.dispatchEvent(new CustomEvent('clearChat'));
  }

  async handleLogout(action) {
    // Dispatch custom event for logout
    window.dispatchEvent(new CustomEvent('triggerLogout'));
  }

  async handleTriggerCustomAction(action) {
    // Dispatch custom event for custom action
    window.dispatchEvent(
      new CustomEvent('triggerCustomAction', {
        detail: {
          actionId: action.actionId,
          label: action.label,
        },
      })
    );
  }

  async handleOpenRenderingModal(action) {
    // Dispatch the existing openRenderingsModal event that the app already listens for
    window.dispatchEvent(
      new CustomEvent('openRenderingsModal', {
        detail: action.payload || action.data || {},
      })
    );
  }

  /**
   * Register a custom action handler
   * @param {string} actionType - Action type to handle
   * @param {Function} handler - Handler function
   */
  registerHandler(actionType, handler) {
    this.handlers.set(actionType, handler);
  }

  /**
   * Unregister a custom action handler
   * @param {string} actionType - Action type to unregister
   */
  unregisterHandler(actionType) {
    this.handlers.delete(actionType);
  }
}

// Create singleton instance
const uiActionHandler = new UIActionHandler();

export default uiActionHandler;
